import { BookkeepingServicePromiseClient } from '@taxfyle/api-internal/internal/bookkeeping_grpc_web_pb'
import {
  CompleteBookkeepingAssessmentReviewRequest,
  ConfirmManagedPlatformAccessRequest,
  GetAssessmentRequest,
  GetBookkeepingPendingActionAggregationsRequest,
  ListBookkeepingPendingActionsRequest,
} from '@taxfyle/api-internal/internal/bookkeeping_pb'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import { createGrpcAuthenticator } from 'infra/grpc-authenticator'

import { memoize } from 'lodash'
import { Observable, filter } from 'rxjs'
import { APIConfig } from 'types/definitions'
import { enableDevTools } from 'utils/grpc-util'

import TypeCase = RealtimeEvent.TypeCase

/**
 * Creates an instance of the Bookkeeping Manager API.
 *
 * @param config - API config.
 * @returns
 */
export function createBookkeepingManagerAPI(
  config: APIConfig,
  events$: Observable<RealtimeEvent>,
) {
  return new BookkeepingManagerAPI(config, events$)
}

export class BookkeepingManagerAPI {
  private readonly bookkeepingManagerClient =
    createBookkeepingManagerGrpcClient(this.workDotnetConfig.baseURL)

  private readonly grpcAuthenticator = createGrpcAuthenticator(
    this.workDotnetConfig,
  )

  /**
   * Stream of events for job progression.
   */
  readonly events$: Observable<RealtimeEvent>

  constructor(
    private workDotnetConfig: APIConfig,
    events$: Observable<RealtimeEvent>,
  ) {
    this.events$ = events$.pipe(
      filter((event) => event.getTypeCase() === TypeCase.BOOKKEEPING_UPDATED),
    )
  }

  /**
   * Fetches bookkeeping pending actions.
   *
   * @param request
   * @returns
   */
  async listBookkeepingPendingActions(
    request: ListBookkeepingPendingActionsRequest,
  ) {
    return this.bookkeepingManagerClient.listBookkeepingPendingActions(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Gets the bookkeeping pending action aggregations.
   *
   * @param request
   * @returns
   */
  async getBookkeepingPendingActionAggregations(
    request: GetBookkeepingPendingActionAggregationsRequest,
  ) {
    return this.bookkeepingManagerClient.getBookkeepingPendingActionAggregations(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Confirms managed platform access.
   *
   * @param request
   * @returns
   */
  async confirmManagedPlatformAccess(
    request: ConfirmManagedPlatformAccessRequest,
  ) {
    return this.bookkeepingManagerClient.confirmManagedPlatformAccess(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Gets bookkeeping assessment.
   *
   * @param request
   * @returns
   */
  async getAssessment(request: GetAssessmentRequest) {
    return this.bookkeepingManagerClient.getAssessment(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Confirms bookkeeping assessment review.
   *
   * @param request
   * @returns
   */
  async completeBookkeepingAssessmentReview(
    request: CompleteBookkeepingAssessmentReviewRequest,
  ) {
    return this.bookkeepingManagerClient.completeBookkeepingAssessmentReview(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }
}

const createBookkeepingManagerGrpcClient = memoize(
  function createBookkeepingManagerGrpcClient(baseUrl: string) {
    const client = new BookkeepingServicePromiseClient(baseUrl)

    enableDevTools(client)

    return client
  },
)
